/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';

// Component imports
import { Button, Checkbox, Col, Divider, Empty, Form, Input, InputNumber, Row, Select, Skeleton, Slider, Tooltip, Typography } from 'antd';
import { InfoCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { DeleteIcon } from '../../../Icons/DeleteIcon';
import { PlusIcon } from '../../../Icons/PlusIcon';
import RichTextEditor from '../../../Forms/Fields/RichTextEditor/RichTextEditor';

// Misc imports
import { getCurrentQueryState } from '../../../../store/utils/getCurrentQueryState';
import { useNavigate, useParams } from 'react-router-dom';
import { GEMINI_MODELS, OPEN_AI_COMPLETION_MODELS, VARIABLE_TYPES } from '../../../../constants/main';
import { useFetchBotQuery } from '../../../../store/bots/botsApi';
import { NAV_BOT } from '../../../../constants/navigation';
import useTheme from '../../../../utils/hooks/useTheme';
import replaceUrlParams from '../../../../utils/string/replaceUrlParams';
import { INTEGRATIONS, INTEGRATION_TYPE, PLUGIN_CATEGORY } from '../../../../constants/integrations';
import { RICH_TEXT_TOOLBAR_ACTIONS } from '../../../../constants/markdown';
import CustomInputNumber from '../../../Forms/Fields/CustomInputNumber';

// Constant declarations
const { Text } = Typography;

const LlmNodeForm = ({ form, setCanSubmit }) => {
  const navigate = useNavigate();
  const temperatureSliderRef = useRef(null);
  const { borderRadiusBase } = useTheme();
  const { orgId, tenantId } = useParams();
  const { t } = useTranslation();
  const prevProviderRef = useRef();

  const { data: bot, isFetching: isFetchingBot } = useFetchBotQuery({ orgId, tenantId });
  const { data: storyVariables, isFetching: isFetchingStoryVariables } = getCurrentQueryState('fetchStoryVariables', { orgId, tenantId });

  const [localVariables, setLocalVariables] = useState([]);
  const [localModels, setLocalModels] = useState([]);

  const isGeminiIntegration = useMemo(() => {
    return form.getFieldValue(['data', 'llm', 'provider']) === INTEGRATION_TYPE.GEMINI_AI_LLM;
  }, [form.getFieldValue(['data', 'llm', 'provider'])]);

  const isAzureIntegration = useMemo(() => {
    return form.getFieldValue(['data', 'llm', 'provider']) === INTEGRATION_TYPE.AZURE_AI_LLM;
  }, [form.getFieldValue(['data', 'llm', 'provider'])]);

  const currentModelListToBeUsed = useMemo(() => {
    return isGeminiIntegration ? GEMINI_MODELS : OPEN_AI_COMPLETION_MODELS;
  }, [isGeminiIntegration]);

  useEffect(() => {
    setLocalModels(Object.values(currentModelListToBeUsed));
  }, [currentModelListToBeUsed]);
  // Whenever the story variables change, update the local variables
  useEffect(() => {
    const validVariables = [...storyVariables.filter(variable => variable.type !== VARIABLE_TYPES.STATIC)];
    setLocalVariables(validVariables);
  }, [storyVariables]);

  const updateVariables = (newVariableName) => {
    if (newVariableName && storyVariables.filter(variable => variable.name === newVariableName).length === 0) {
      setLocalVariables([...new Set([...storyVariables.filter(variable => variable.type !== VARIABLE_TYPES.STATIC), { name: newVariableName, type: '' }])]);
      form.setFieldsValue({ data: { variable: { key: newVariableName } } }); // Update the form value
      form.validateFields([{ data: { variable: { key: newVariableName } } }]);
    }
  };

  const updateModels = useCallback((newModelName) => {
    if (newModelName && Object.values(currentModelListToBeUsed).filter(model => model === newModelName).length === 0) {
      setLocalModels([...new Set([...Object.values(currentModelListToBeUsed), newModelName])]);
    }
  }, [currentModelListToBeUsed]);

  return (
    <Fragment>
      {
        isFetchingBot ? (
          <Skeleton active />
        ) : bot?.plugins?.filter((plugin) => plugin.category === PLUGIN_CATEGORY.LLM_NODE).length > 0 ? (
          <Fragment>

            <Row gutter={16} className="mb-4">
              <Col xs={24}>
                <Form.Item
                  className="mb-1"
                  label= {t('Friendly name')}
                  name={['data', 'friendlyName']}
                >
                  <Input
                    placeholder={t('Enter a short friendly name')}
                    size="large"
                    maxLength={30}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Divider orientation="left" className="mb-6 mt-0">
              <Text type="secondary" className="fs-base">{t('LLM Configuration')}</Text>
            </Divider>

            <Row gutter={16} className="mb-4">
              <Col xs={24}>
                <Form.Item
                  label={t('LLM Plugin')}
                  name={['data', 'llm', 'provider']}
                  rules={[
                    {
                      required: true,
                      message: t('Provider cannot be blank.')
                    }]}
                >
                  <Select
                    placeholder={t('Select an LLM provider')}
                    onChange={(value) => {

                      const previousProvider = prevProviderRef.current;
                      prevProviderRef.current = value;

                      const wasGemini = previousProvider === INTEGRATION_TYPE.GEMINI_AI_LLM;
                      const isGemini = value === INTEGRATION_TYPE.GEMINI_AI_LLM;

                      if ((wasGemini && !isGemini) || (!wasGemini && isGemini)) {
                        form.setFieldsValue({
                          data: {
                            llm: {
                              configuration: {
                                model: undefined
                              }
                            }
                          }
                        });
                      }
                    }}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                    options={bot?.plugins.filter((plugin) => plugin.category === 'LLM_NODE').map((llm) => {
                      return {
                        value: llm.integration.type,
                        label: t( Object.values(INTEGRATIONS).find(integration => integration.type === llm.integration.type).type
                          , { context: 'type' })
                      };
                    })}
                  />
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'llm', 'configuration', 'model']}
                  label={isAzureIntegration ? t('Deployment') : t('Model')}
                  labelAlign="left"
                  required
                  rules={[
                    {
                      required: true,
                      message: t('Model cannot be blank.')
                    }]}
                  className="mb-0"
                >
                  <Select
                    showSearch
                    onSearch={updateModels}
                    onBlur={(e) => updateModels(e.target.value)}
                    placeholder={isAzureIntegration ? t('Type / Select deployment') : t('Type / Select model')}
                    getPopupContainer={triggerNode => triggerNode.parentElement}
                    options={(localModels || [])
                      .map((element) => {
                        return {
                          value: element,
                          label: element
                        };
                      })
                    // Keep only unique values
                      .filter((elem, index, self) => self.findIndex((t) => {
                        return (t.value === elem.value);
                      }) === index)
                    }
                  />
                </Form.Item>
              </Col>
              <Col xs={24} className="mt-4">
                <Form.Item
                  label={t('Prompt')}
                  required
                  name={['data', 'llm', 'configuration', 'prompt']}
                  rules={[
                    {
                      required: true,
                      message: t('Prompt cannot be blank.')
                    },
                    {
                      validator: (_, value) => {
                        const isResponseInJson = form.getFieldValue(['data', 'llm', 'configuration', 'isResponseInJson']);

                        const jsonInstructionPattern = /json/i;
                        const hasJsonInstruction = jsonInstructionPattern.test(value);

                        const jsonObjectPattern = /(?<!{){(?!{)[^]*?}/;
                        const hasJsonObject = jsonObjectPattern.test(value);

                        const rejectWithError = (message) => {
                          setCanSubmit(false);
                          return Promise.reject(new Error(message));
                        };
                        if (isResponseInJson) {
                          if (!hasJsonInstruction) {
                            return rejectWithError('In order to submit, you must instruct the agent to respond in JSON.');
                          }
                          if (!hasJsonObject) {
                            return rejectWithError('In order to submit, you must include a JSON-like object in your prompt.');
                          }
                        } else {
                          if (hasJsonInstruction && !hasJsonObject) {
                            return rejectWithError('You need to remove the JSON instruction, or enable the "Response is in JSON" checkbox.');
                          }
                          if (!hasJsonInstruction && hasJsonObject) {
                            return rejectWithError('You either need to remove the JSON object, or enable the "Response is in JSON" checkbox.');
                          }
                          if (hasJsonInstruction && hasJsonObject) {
                            return rejectWithError('You either need to remove both the JSON instruction and the JSON object, or enable the "Response is in JSON" checkbox.');
                          }
                        }
                        setCanSubmit(true);
                        return Promise.resolve();
                      }
                    }
                  ]}
                >
                  <RichTextEditor
                    toolbarActions={[
                      RICH_TEXT_TOOLBAR_ACTIONS.HEADING,
                      RICH_TEXT_TOOLBAR_ACTIONS.BOLD,
                      RICH_TEXT_TOOLBAR_ACTIONS.ITALIC,
                      RICH_TEXT_TOOLBAR_ACTIONS.BULLET_LIST,
                      RICH_TEXT_TOOLBAR_ACTIONS.ORDERED_LIST,
                      RICH_TEXT_TOOLBAR_ACTIONS.CUSTOM_VARIABLE
                    ]}
                    copySourceButton
                    placeholder={t('Enter the prompt')}
                    elementStyles={{ p: { marginBottom: 0 } }}
                  />
                </Form.Item>
              </Col>

              <Col xs={24}>
                <div ref={temperatureSliderRef}>
                  <Form.Item
                    label={t('Temperature')}
                    name={['data', 'llm', 'configuration', 'temperature']}
                  >
                    <Slider
                      min={0}
                      max={2}
                      step={0.1}
                      className="custom-slider mx-6"
                      marks={{ 0: '0', 2: '2' }}
                      handleStyle={{ borderRadius: borderRadiusBase }}
                      onChange={(value) => {
                        const sliderHandler = temperatureSliderRef.current.querySelector('.ant-slider-handle');
                        if (!sliderHandler) {
                          return;
                        }
                        sliderHandler.innerHTML = value;
                      }}
                    />
                  </Form.Item>
                </div>
              </Col>

              <Col xs={24}>
                <Form.Item
                  label={t('Max Tokens')}
                  name={['data', 'llm', 'configuration', 'maxTokens']}
                  tooltip={{
                    title: (
                      <Typography.Text type="secondary">{t('The maximum number of tokens to use when invoking the LLM.')}</Typography.Text>
                    ),
                    icon: <InfoCircleOutlined />
                  }}
                >
                  <InputNumber min={1} defaultValue={50}/>
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={['data', 'llm', 'configuration', 'includeHistory']}
                  valuePropName="checked"
                  className="mb-2"
                >
                  <Checkbox>
                    <Text>{t('Include history')}</Text>
                    <Tooltip title={t('The LLM will include the session history when generating the response.')}>
                      <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'llm', 'configuration', 'maxHistoryTurns']}
                  label={
                    <Fragment>
                      <Text>{t('Maximum history turns')}</Text>
                      <Tooltip title={t('A turn is a group of messages starting with one or more consecutive user messages followed by any agent responses until the next user message.')}>
                        <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                      </Tooltip>
                    </Fragment>
                  }
                  validateTrigger={['onChange', 'onBlur']}
                  className="mb-0"
                >
                  <CustomInputNumber
                    className="w-10"
                    disabled={!form.getFieldValue(['data', 'llm', 'configuration', 'includeHistory'])}
                    type="number"
                    min={1}
                    max={30}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Divider orientation="left" className="my-6">
              <Text type="secondary" className="fs-base">{t('LLM Response')}</Text>
              <Tooltip title={t('Configure how the LLM response should be used.')}>
                <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
              </Tooltip>

            </Divider>

            <Row gutter={16}>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'llm', 'variable']}
                  label={
                    <Row>
                      <Col span={24}>
                        <Text>
                          {t('Store response as a variable')}
                        </Text>
                        <Tooltip title={t('Save the LLM\'s response as a variable (value) for use in message templates or control flow nodes.')}>
                          <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                        </Tooltip>

                      </Col>
                      <Col span={24} className="mb-2">
                        <Text type="secondary" className="fs-sm mr-3">
                          {t('Select a variable (name) or type to create custom.')}
                        </Text>
                      </Col>
                    </Row>
                  }
                  rules={[
                    {
                      pattern: /^[a-zA-Z0-9_$\\.]*$/,
                      message: t('Variable names can only use letters, numbers, underscores, dollar signs, and dots.')
                    }]
                  }
                >
                  {
                    storyVariables && localVariables ? (
                      <Select
                        showSearch
                        className="w-100"
                        placeholder={t('Type / select variable')}
                        // When the user types a new variable name, add it to the list of local variables
                        onSearch={updateVariables}
                        getPopupContainer={triggerNode => triggerNode.parentElement}
                        onBlur={(e) => updateVariables(e.target.value)}
                        loading={isFetchingStoryVariables}
                        options={(localVariables || [])
                          .filter((variable) => {
                            return ![VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.LIVECHAT, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.CSAT].includes(variable.type);
                          })
                          .map((element) => {
                            return {
                              value: element.name,
                              label: element.name
                            };
                          })
                        // Keep only unique values
                          .filter((elem, index, self) => self.findIndex((t) => {
                            return (t.value === elem.value);
                          }) === index)
                        }
                      />
                    ) : null
                  }
                </Form.Item>
              </Col>

              <Col xs={24}>
                <Form.Item
                  name={['data', 'llm', 'sendResponse']}
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox>
                    <Text>{t('Send LLM Response to user.')}</Text>
                    <Tooltip title={t('The agent will directly present the LLM\'s response to the user.')}>
                      <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'llm', 'configuration', 'isResponseInJson']}
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox
                    onChange={(e) => {
                      const isChecked = e.target.checked;
                      form.setFieldsValue({ data: { llm: { configuration: { isResponseInJson: isChecked } } } });
                      form.validateFields([['data', 'llm', 'configuration', 'prompt']]);
                    }}
                  >
                    {t('Response is in JSON')}
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                <Form.Item
                  name={['data', 'llm', 'showTypingIndicator']}
                  valuePropName="checked"
                  className="mb-0"
                >
                  <Checkbox
                    defaultChecked={true}
                  >
                    <Text>{t('Show Typing Indicator')}</Text>
                    <Tooltip title={t('Shows a typing indicator to the end-user while the agent is generating a response.')}>
                      <QuestionCircleOutlined className="ml-2 fs-md text-secondary" />
                    </Tooltip>
                  </Checkbox>
                </Form.Item>
              </Col>
              <Col xs={24}>
                {
                  form.getFieldValue(['data', 'llm', 'configuration', 'isResponseInJson'])
                    ? <Form.Item
                      shouldUpdate
                      label={t('Extract Parameters')}
                      className="my-4"
                      tooltip={{
                        icon: <QuestionCircleOutlined />,
                        title: t('Specify the parameters to be extracted from the LLM\'s response.')
                      }}
                    >
                      <Form.List
                        name={['data', 'llm', 'configuration', 'extractParameters']}
                      >
                        {(fields, { add, remove }) => (
                          <Fragment>
                            {fields.map((field) => {
                              return (
                                <Row gutter={16} >
                                  <Col flex="auto">
                                    <Form.Item
                                      label={t('JSON Property')}
                                      name={[field.name, 'key']}
                                      tooltip={{
                                        icon: <QuestionCircleOutlined />,
                                        title: t('Enter a property found in the top-level of the JSON response from the LLM.')
                                      }}
                                    >
                                      <Input placeholder={t('Select Data')} />
                                    </Form.Item>
                                  </Col>

                                  <Col flex="auto">
                                    <Form.Item
                                      label={t('Variable Name')}
                                      name={[field.name, 'value']}
                                      tooltip={{
                                        icon: <QuestionCircleOutlined />,
                                        title: t('Enter the variable name to store the extracted JSON property.')
                                      }}
                                    >
                                      {
                                        storyVariables && localVariables ? (
                                          <Select
                                            showSearch
                                            className="w-90"
                                            placeholder={t('Type / select variable')}
                                            // When the user types a new variable name, add it to the list of local variables
                                            onSearch={updateVariables}
                                            getPopupContainer={triggerNode => triggerNode.parentElement}
                                            onBlur={(e) => updateVariables(e.target.value)}
                                            loading={isFetchingStoryVariables}
                                            options={(localVariables || [])
                                              .filter((variable) => {
                                                return ![VARIABLE_TYPES.QUESTION, VARIABLE_TYPES.SYSTEM, VARIABLE_TYPES.LIVECHAT, VARIABLE_TYPES.CONTACT, VARIABLE_TYPES.CSAT].includes(variable.type);
                                              })
                                              .map((element) => {
                                                return {
                                                  value: element.name,
                                                  label: element.name
                                                };
                                              })
                                            // Keep only unique values
                                              .filter((elem, index, self) => self.findIndex((t) => {
                                                return (t.value === elem.value);
                                              }) === index)
                                            }
                                          />
                                        ) : null
                                      }
                                    </Form.Item>
                                  </Col>

                                  <Col flex="none">
                                    <Button type="text" onClick={() => remove(field.name)} icon={<DeleteIcon />} />
                                  </Col>
                                </Row>
                              );
                            })}

                            <Button type="dashed" onClick={() => add()} icon={<PlusIcon />}>
                              {t('Add Parameter')}
                            </Button>
                          </Fragment>
                        )}
                      </Form.List>
                    </Form.Item> : null
                }
              </Col>
            </Row>
          </Fragment>
        ) : (
          <Empty
            description={
              <Text type="secondary">
                {t('No plugin enabled. Please')}
                <Button
                  type="link"
                  className="px-1"
                  onClick={() => {
                    navigate(`${replaceUrlParams(NAV_BOT, { orgId, tenantId: tenantId })}?view=plugins&scope=LLM`);
                  }}
                >
                  {t('visit the plugins page')}
                </Button>
                {t('to enable a new one.')}
              </Text>}
          />
        )
      }
    </Fragment>
  );
};

LlmNodeForm.propTypes = {
  form: PropTypes.object,
  setCanSubmit: PropTypes.func.isRequired
};

export default LlmNodeForm;
